import type { WhitespaceGridCell, WhitespaceItem } from 'hooks/useWhitespaceAnalysisGridData';

export const EMPTY_VALUE = '-';

const normalize = (value?: string) => (value ?? '').trim().toLowerCase();
const normalizeAttributeType = (value?: string) => normalize(value).replace(/\s+/g, '_');

const parseNumericLike = (value: string | number | boolean | null | undefined) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value == null) return null;
    const cleaned = String(value).replace(/,/g, '').replace(/[^\d.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const isCompactScaleSuffix = (suffix?: string) => {
    const normalized = (suffix ?? '').trim().toLowerCase();
    return normalized === 'k' || normalized === 'm' || normalized === 'b';
};

const isIntegerUnitSuffix = (suffix?: string) => {
    const normalized = (suffix ?? '').trim().toLowerCase();
    return normalized === 'ct' || normalized === 'oz';
};

export const getDecimalPlacesForSuffix = (suffix?: string) => (isIntegerUnitSuffix(suffix) ? 0 : 2);

const formatDecimal = (value: number, decimalPlaces: number, useGrouping = true) =>
    value.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
        useGrouping,
    });

const formatNumericWithAffixes = ({
    value,
    prefix,
    suffix,
    decimalPlaces = 2,
    useGrouping = true,
}: {
    value: number;
    prefix?: string;
    suffix?: string;
    decimalPlaces?: number;
    useGrouping?: boolean;
}) => `${prefix ?? ''}${formatDecimal(value, decimalPlaces, useGrouping)}${suffix ?? ''}`;

const stripMagnitudeSuffix = (suffix?: string) => {
    const normalized = (suffix ?? '').trim().toLowerCase();
    return normalized === 'k' || normalized === 'm' || normalized === 'b' ? '' : suffix;
};

const getKpiByIds = (item: WhitespaceItem | undefined, kpiIds: string[]) => {
    if (!item?.kpis?.length || kpiIds.length === 0) return undefined;
    const kpiIdSet = new Set(kpiIds.map(normalize));
    return item.kpis.find((kpi) => kpiIdSet.has(normalize(kpi.kpi_id)));
};

const extractAffixesAroundNumber = (raw: string) => {
    const firstDigitIdx = raw.search(/\d/);
    if (firstDigitIdx === -1) return { prefix: '', suffix: '' };
    const lastDigitIdx = raw.search(/\d(?=\D*$)/);
    return {
        prefix: raw.slice(0, firstDigitIdx).trim(),
        suffix: raw.slice(lastDigitIdx + 1).trim(),
    };
};

const formatRangeBound = (value: number, decimalPlaces: number) => {
    if (decimalPlaces === 0 && Number.isInteger(value)) return String(value);
    return value.toFixed(decimalPlaces).replace(/\.?0+$/, '');
};

const formatPercent = (value?: number | null) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return EMPTY_VALUE;
    return `${value.toFixed(2)}%`;
};

export const buildWhitespaceExcelFormat = ({
    prefix = '',
    suffix = '',
    decimalPlaces = 2,
    isPercent = false,
    useGrouping = true,
}: {
    prefix?: string;
    suffix?: string;
    decimalPlaces?: number;
    isPercent?: boolean;
    useGrouping?: boolean;
}) => {
    if (isPercent) {
        const decimalPart = decimalPlaces > 0 ? `.${'0'.repeat(decimalPlaces)}` : '';
        return `0${decimalPart}"%"`;
    }

    const decimalPart = decimalPlaces > 0 ? `.${'0'.repeat(decimalPlaces)}` : '';
    const prefixPart = prefix ? `"${prefix}"` : '';
    const normalizedSuffix = suffix?.trim() ?? '';
    const suffixPart =
        normalizedSuffix && !isCompactScaleSuffix(normalizedSuffix) ? `"${normalizedSuffix}"` : '';
    const integerPart = useGrouping ? '#,##0' : '0';
    return `${prefixPart}${integerPart}${decimalPart}${suffixPart}`;
};

export const toWhitespaceExportCellValue = (raw: number | null, display: string): string | number =>
    raw != null && Number.isFinite(raw) ? raw : display;

const getItemDefaultCrossSectionValue = (item: WhitespaceItem) => item.description ?? item.ex_id ?? EMPTY_VALUE;

const getHierarchyCrossSectionMatch = (item: WhitespaceItem, crossSectionKey: string) =>
    item.hierarchy?.find((entry) => normalizeAttributeType(entry.attribute_type) === crossSectionKey) ?? null;

const VENDOR_FALLBACK_KEYS = ['vendor', 'manufacturer', 'company'];

const isVendorLikeType = (attributeType?: string) => {
    const normalizedType = normalize(attributeType);
    return normalizedType === 'mfr' || VENDOR_FALLBACK_KEYS.some((keyword) => normalizedType.includes(keyword));
};

const getVendorFallbackHierarchyMatch = (item: WhitespaceItem) =>
    item.hierarchy?.find((entry) => isVendorLikeType(entry.attribute_type)) ?? null;

export const getRawKpiValue = (item: WhitespaceItem | undefined, kpiIds: string[]) => {
    const match = getKpiByIds(item, kpiIds);
    if (!match) return null;

    if (typeof match.raw_value === 'number' && Number.isFinite(match.raw_value)) {
        return match.raw_value;
    }

    return parseNumericLike(match.value);
};

export const getFormattedKpiValue = (
    item: WhitespaceItem | undefined,
    kpiIds: string[],
    options?: { stripMagnitudeSuffix?: boolean; useGrouping?: boolean },
) => {
    const match = getKpiByIds(item, kpiIds);
    if (!match) return EMPTY_VALUE;

    const numericValue = getRawKpiValue(item, kpiIds);
    if (numericValue !== null) {
        const suffix = options?.stripMagnitudeSuffix ? stripMagnitudeSuffix(match.valueSuffix) : match.valueSuffix;
        return formatNumericWithAffixes({
            value: numericValue,
            prefix: match.valuePrefix,
            suffix,
            decimalPlaces: getDecimalPlacesForSuffix(suffix),
            useGrouping: options?.useGrouping ?? true,
        });
    }

    if (match.value == null || String(match.value).trim() === '') return EMPTY_VALUE;
    return `${match.valuePrefix ?? ''}${String(match.value)}${match.valueSuffix ?? ''}`;
};

export const getYAxisRangeRawValue = (bucketLabel: string) =>
    bucketLabel && bucketLabel !== EMPTY_VALUE ? bucketLabel : EMPTY_VALUE;

export const formatYAxisRange = ({
    bucketLabel,
    item,
    selectedYAxisKpiId,
}: {
    bucketLabel: string;
    item?: WhitespaceItem;
    selectedYAxisKpiId?: string;
}) => {
    if (!bucketLabel || bucketLabel === EMPTY_VALUE) return EMPTY_VALUE;
    // Split on the separator `-` between two numbers, supporting negative bounds (e.g. "-10-0").
    const rangeMatch = bucketLabel.match(/^(-?[^-]+?)\s*-\s*(-?[^-]+)$/);
    if (!rangeMatch) return bucketLabel;

    const leftRaw = rangeMatch[1]?.trim() ?? '';
    const rightRaw = rangeMatch[2]?.trim() ?? '';
    const leftNum = parseNumericLike(leftRaw);
    const rightNum = parseNumericLike(rightRaw);
    if (leftNum === null || rightNum === null) return bucketLabel;

    const leftAffixes = extractAffixesAroundNumber(leftRaw);
    const rightAffixes = extractAffixesAroundNumber(rightRaw);
    const yAxisKpi = getKpiByIds(item, selectedYAxisKpiId ? [selectedYAxisKpiId] : []);
    const prefix = leftAffixes.prefix || rightAffixes.prefix || yAxisKpi?.valuePrefix || '';
    const suffix = leftAffixes.suffix || rightAffixes.suffix || yAxisKpi?.valueSuffix || '';
    const decimalPlaces = getDecimalPlacesForSuffix(suffix);

    return `${prefix}${formatRangeBound(leftNum, decimalPlaces)}${suffix} - ${prefix}${formatRangeBound(rightNum, decimalPlaces)}${suffix}`;
};

export const getCrossSectionValue = (item: WhitespaceItem | undefined, crossSection: string) => {
    if (!item) return EMPTY_VALUE;
    const crossSectionKey = normalizeAttributeType(crossSection);
    if (!crossSectionKey) return EMPTY_VALUE;

    const ownType = normalizeAttributeType(item.attribute_type);
    if (ownType === crossSectionKey) {
        return getItemDefaultCrossSectionValue(item);
    }

    const hierarchyMatch = getHierarchyCrossSectionMatch(item, crossSectionKey);
    if (hierarchyMatch) {
        return hierarchyMatch.description ?? hierarchyMatch.ex_id ?? EMPTY_VALUE;
    }

    if (crossSectionKey !== 'vendor') {
        return getItemDefaultCrossSectionValue(item);
    }

    const vendorFallbackMatch = getVendorFallbackHierarchyMatch(item);
    if (vendorFallbackMatch) {
        return vendorFallbackMatch.description ?? vendorFallbackMatch.ex_id ?? EMPTY_VALUE;
    }

    return getItemDefaultCrossSectionValue(item);
};

export const getRawCellValueSales = (cell: WhitespaceGridCell) => {
    if (typeof cell.raw_value === 'number' && Number.isFinite(cell.raw_value)) {
        return cell.raw_value;
    }
    return parseNumericLike(cell.valueSales);
};

export const getFormattedValueSales = (item: WhitespaceItem | undefined) =>
    getFormattedKpiValue(item, ['value_sales', 'activate_value_sales'], { stripMagnitudeSuffix: true, useGrouping: false });

export const getCellValueSales = (cell: WhitespaceGridCell) => {
    const numericCellValue = getRawCellValueSales(cell);
    if (numericCellValue === null) return EMPTY_VALUE;
    return formatNumericWithAffixes({
        value: numericCellValue,
        prefix: cell.valuePrefix,
        suffix: stripMagnitudeSuffix(cell.valueSuffix),
        useGrouping: false,
    });
};

export const getRawValueSalesShareInCell = (item: WhitespaceItem | undefined, cell: WhitespaceGridCell) => {
    const itemValueSalesKpi = getKpiByIds(item, ['value_sales', 'activate_value_sales']);
    const itemRawValue = itemValueSalesKpi?.raw_value;
    if (typeof itemRawValue !== 'number' || !Number.isFinite(itemRawValue)) return null;
    if (typeof cell.raw_value !== 'number' || !Number.isFinite(cell.raw_value) || cell.raw_value === 0) {
        return null;
    }
    return (itemRawValue / cell.raw_value) * 100;
};

export const getValueSalesShareInCell = (item: WhitespaceItem | undefined, cell: WhitespaceGridCell) =>
    formatPercent(getRawValueSalesShareInCell(item, cell));

export const getRawValueSalesGrowthVsYa = (item: WhitespaceItem | undefined) => {
    const itemValueSalesKpi = getKpiByIds(item, ['value_sales', 'activate_value_sales']);
    if (
        typeof itemValueSalesKpi?.growth_percentage === 'number' &&
        Number.isFinite(itemValueSalesKpi.growth_percentage)
    ) {
        return itemValueSalesKpi.growth_percentage;
    }
    return null;
};

export const getValueSalesGrowthVsYa = (item: WhitespaceItem | undefined) =>
    formatPercent(getRawValueSalesGrowthVsYa(item));

const formatWhitespaceDisplayValue = (value: number, format: string): string => {
    if (format.includes('"%"')) {
        const decimalPlaces = format.includes('.00') ? 2 : format.includes('.0') ? 1 : 0;
        return `${value.toFixed(decimalPlaces)}%`;
    }

    const prefixMatch = format.match(/^"([^"]*)"/);
    const prefix = prefixMatch?.[1] ?? '';
    const suffixMatch = format.match(/"([^"]*)"$/);
    const suffix = suffixMatch?.[1] ?? '';
    const decimalPlaces = format.includes('.00') ? 2 : format.includes('.0') ? 1 : 0;
    const useGrouping = format.includes('#,##');
    const formatted = value.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
        useGrouping,
    });

    return `${prefix}${formatted}${suffix}`;
};

export const formatWhitespaceExportRowsForCsv = (
    rows: Array<Array<string | number>>,
    columnFormats: Array<string | null>,
    headerRowIndex: number,
): Array<Array<string | number>> =>
    rows.map((row, rowIndex) => {
        if (rowIndex <= headerRowIndex) return row;

        return row.map((cell, colIndex) => {
            const format = columnFormats[colIndex];
            if (cell === '' || cell === EMPTY_VALUE || format == null || typeof cell !== 'number') {
                return cell;
            }

            return formatWhitespaceDisplayValue(cell, format);
        });
    });
